import { OpenAPI } from "@propertelligent/client-api";
import { UserLoginData } from "../../common/charts/Enums";
import { EmailProviderType } from "../../../utils/emailProviderType";
export { EmailProviderType };

/**
 * Raw-fetch bridge to the organization email (OrganizationEmailController) endpoints.
 *
 * The typed client methods do not exist until CI regenerates @propertelligent/client-api, so the
 * "My Organization > Email" connect/manage screen and its OAuth-return handler reach the new
 * /api/OrganizationEmail/* endpoints through these helpers. Centralizing them here keeps the
 * components presentational and gives one place to swap in the generated OrganizationEmailService
 * after regen (mirrors platform-portal/payment-accounts/platformPaymentAccountsApi.ts).
 *
 * TODO: replace the fetch calls below with the generated OrganizationEmailService once client-api
 * is regenerated.
 */

export interface OrganizationEmailStatus {
  isConnected: boolean;
  emailAddress: string | null;
  providerType: EmailProviderType;
  smtpHost: string | null;
  smtpUsername: string | null;
  fromName: string | null;
  fromEmail: string | null;
}

export interface SmtpSettingsRequest {
  host: string;
  port: number;
  username: string;
  password: string;
  useSsl: boolean;
  fromName: string;
  fromEmail: string;
}

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem(UserLoginData.token)}`,
});

/**
 * Fetch the organization's Gmail connection status and normalize the envelope into a flat
 * { isConnected, emailAddress } shape. Always resolves: a non-ok response or a network/parse
 * failure is logged and reported as the disconnected fallback so the caller can render safely.
 */
export const getStatus = async (): Promise<OrganizationEmailStatus> => {
  try {
    const res = await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/status`, {
      method: "GET",
      headers: authHeaders(),
    });
    if (!res.ok) {
      throw res;
    }
    const body = await res.json();
    return {
      isConnected: !!body?.success?.isConnected,
      emailAddress: body?.success?.emailAddress ?? null,
      providerType: body?.success?.providerType ?? EmailProviderType.None,
      smtpHost: body?.success?.smtpHost ?? null,
      smtpUsername: body?.success?.smtpUsername ?? null,
      fromName: body?.success?.fromName ?? null,
      fromEmail: body?.success?.fromEmail ?? null,
    };
  } catch (error) {
    console.error("Failed to load organization email status:", error);
    return {
      isConnected: false,
      emailAddress: null,
      providerType: EmailProviderType.None,
      smtpHost: null,
      smtpUsername: null,
      fromName: null,
      fromEmail: null,
    };
  }
};

/**
 * Disconnect the organization's Gmail account. Resolves on success; throws the non-ok response so
 * the caller can surface the failure (the manage screen logs it and leaves the current status).
 */
export const disconnect = async (): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/disconnect`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Exchange the Google OAuth authorization code for stored organization credentials. Posts the code
 * and the redirect URI used to obtain it. Rejections propagate so the OAuth-return handler can log
 * them before navigating back to the manage screen.
 */
export const saveRequestToken = async (
  code: string,
  redirectUri: string
): Promise<void> => {
  await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/save-request-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionStorage.getItem(UserLoginData.token)}`,
    },
    body: JSON.stringify({
      authorizationToken: code,
      redirectUri,
    }),
  });
};

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${sessionStorage.getItem(UserLoginData.token)}`,
});

/**
 * Exchange the Microsoft (Azure AD / Outlook) OAuth authorization code for stored organization
 * credentials. Mirrors saveRequestToken above (Gmail) — connecting Outlook supersedes any
 * previously-connected Gmail/SMTP account for this organization (single-provider-per-org, enforced
 * server-side). Rejections propagate so the OAuth-return handler can log them before navigating back.
 */
export const saveOutlookRequestToken = async (
  code: string,
  redirectUri: string
): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/save-outlook-request-token`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      authorizationToken: code,
      redirectUri,
    }),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Persist the organization's custom SMTP settings. Connecting SMTP supersedes any previously-connected
 * Gmail account for this organization (single-provider-per-org, enforced server-side). Throws the
 * non-ok response on failure so the caller can surface validation errors from the response envelope.
 */
export const saveSmtpSettings = async (request: SmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/save-smtp-settings`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Send a live test email using the given (not-yet-saved) SMTP settings, without persisting them.
 * Lets the Admin confirm credentials work before committing them. Throws the non-ok response on failure.
 */
export const testSmtpConnection = async (request: SmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/OrganizationEmail/test-smtp-connection`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Fetch the Azure AD application (client) id used to build the Microsoft OAuth consent URL.
 * Raw-fetch bridge: `IntegrationsService.postApiIntegrationsKeysMicrosoftClientId()` doesn't exist yet
 * in the generated @propertelligent/client-api (mirrors the existing gmail-client-id method, which the
 * Gmail flow already calls directly) — swap to the generated method once client-api is regenerated.
 * Resolves to an empty string on failure so the caller can render safely without a client id.
 */
export const getMicrosoftClientId = async (): Promise<string> => {
  try {
    const res = await fetch(`${OpenAPI.BASE}/api/Integrations/keys/microsoft-client-id`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!res.ok) {
      throw res;
    }
    const body = await res.json();
    return body?.success ?? "";
  } catch (error) {
    console.error("Failed to load Microsoft client id:", error);
    return "";
  }
};
